import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { AdminLayout, ParticipantLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  GraduationCap, Send, User as UserIcon, Bot, Plus, Trash2, Upload, FileText,
  Loader2, BookOpen, Wrench, Pencil, Download, Sparkles, ArrowLeft,
} from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const generateSessionId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const SUGGESTIONS_BY_SUBJECT = {
  ntg: [
    "Berechne die Kraft auf einen Hebel mit l1=200mm, l2=600mm, F2=150N.",
    "Was ist Newtons 2. Gesetz und wie wird es angewendet?",
    "Wie berechne ich die Spannung in einem Schraubenbolzen?",
  ],
  bwh: [
    "Erkläre den Unterschied zwischen Fixkosten und variablen Kosten.",
    "Wie berechne ich den Deckungsbeitrag?",
    "Was ist eine Break-even-Analyse?",
  ],
  rbh: [
    "Wann darf eine Kündigung außerordentlich erfolgen?",
    "Was sind Pflichten eines Ausbilders nach BBiG?",
    "Erkläre die Produkthaftung kurz für einen Meister.",
  ],
  mikp: [
    "Erkläre den PDCA-Zyklus mit einem Beispiel.",
    "Wie führe ich ein konstruktives Mitarbeitergespräch?",
    "Was ist eine SWOT-Analyse?",
  ],
  zib: [
    "Wie motiviere ich ein Team mit unterschiedlichen Charakteren?",
    "Was sind Hauptaufgaben eines Meisters in der Mitarbeiterführung?",
    "Erkläre die Maslowsche Bedürfnispyramide.",
  ],
  fertigungstechnik: [
    "Berechne die Schnittgeschwindigkeit beim Drehen (d=50mm, n=800/min).",
    "Was ist der Unterschied zwischen Drehen und Fräsen?",
    "Erkläre die Härteprüfverfahren Brinell, Rockwell und Vickers.",
  ],
  montagetechnik: [
    "Was sind die Schritte einer effizienten Montageplanung?",
    "Erkläre den Unterschied zwischen Schraub- und Schweißverbindungen.",
    "Wann nutzt man eine Pressverbindung?",
  ],
  betriebstechnik: [
    "Erkläre TPM (Total Productive Maintenance) kurz.",
    "Was ist Instandhaltungsstrategie zustandsorientiert?",
    "Berechne die Anlagenverfügbarkeit (MTBF, MTTR).",
  ],
  kostenwesen: [
    "Berechne die Maschinenstundensatzrechnung.",
    "Was ist Vollkosten- vs. Teilkostenrechnung?",
    "Wie kalkuliere ich einen Selbstkostenpreis?",
  ],
};

// Render assistant content: extract <svg>...</svg> blocks as raw HTML, keep rest as text
const ContentRenderer = ({ content, role }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Typeset MathJax after content is rendered
    if (window.MathJax && window.MathJax.typesetPromise && containerRef.current) {
      window.MathJax.typesetPromise([containerRef.current]).catch(() => {});
    }
  }, [content]);

  if (role === "user") {
    return (
      <div ref={containerRef} className="whitespace-pre-wrap break-words text-sm">
        {content}
      </div>
    );
  }

  // Split into segments: text + svg blocks
  const segments = [];
  const svgRegex = /<svg[\s\S]*?<\/svg>/gi;
  let lastIndex = 0;
  let match;
  while ((match = svgRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: content.slice(lastIndex, match.index) });
    }
    segments.push({ type: "svg", value: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: "text", value: content.slice(lastIndex) });
  }

  return (
    <div ref={containerRef} className="text-sm leading-relaxed space-y-2 break-words">
      {segments.map((seg, idx) =>
        seg.type === "svg" ? (
          <div
            key={idx}
            className="my-2 p-2 bg-white rounded-sm border border-border inline-block max-w-full overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: seg.value }}
          />
        ) : (
          <div key={idx} className="whitespace-pre-wrap">{seg.value}</div>
        )
      )}
    </div>
  );
};

const SubjectPicker = ({ subjects, bereich, fach, onSelectBereich, onSelectFach }) => {
  if (!subjects) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!bereich) {
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(subjects).map(([key, data]) => (
          <button
            key={key}
            onClick={() => onSelectBereich(key)}
            className="group text-left p-6 rounded-sm border border-border bg-card hover:border-primary/60 transition-colors"
            data-testid={`bereich-${key}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-sm bg-primary/15 flex items-center justify-center">
                {key === "bq" ? <BookOpen className="h-5 w-5 text-primary" /> : <Wrench className="h-5 w-5 text-primary" />}
              </div>
              <div>
                <p className="text-lg font-semibold">{data.label}</p>
                <p className="text-xs text-muted-foreground">{Object.keys(data.subjects).length} Fächer</p>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5 mt-2">
              {Object.values(data.subjects).map((s) => (
                <div key={s} className="flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-primary"></span>
                  <span className="truncate">{s}</span>
                </div>
              ))}
            </div>
          </button>
        ))}
      </div>
    );
  }

  const fachList = subjects[bereich]?.subjects || {};
  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => onSelectBereich(null)} className="mb-3 gap-2" data-testid="back-to-bereich-btn">
        <ArrowLeft className="h-4 w-4" /> Zurück zur Bereichsauswahl
      </Button>
      <p className="text-sm text-muted-foreground mb-3">Wähle ein Fach in {subjects[bereich].label}:</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(fachList).map(([key, label]) => (
          <button
            key={key}
            onClick={() => onSelectFach(key)}
            className={`text-left p-4 rounded-sm border transition-colors ${
              fach === key
                ? "border-primary bg-primary/10"
                : "border-border bg-card hover:border-primary/60"
            }`}
            data-testid={`fach-${key}`}
          >
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm font-medium">{label}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const KnowledgeSidebar = ({ bereich, fach, isAdmin, subjects, onUpload }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadBereich, setUploadBereich] = useState(bereich || "bq");
  const [uploadFach, setUploadFach] = useState(fach || "ntg");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (bereich) params.bereich = bereich;
      if (fach) params.fach = fach;
      const res = await axios.get(`${API}/meister/files`, { params });
      setFiles(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [bereich, fach]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (bereich) setUploadBereich(bereich);
    if (fach) setUploadFach(fach);
  }, [bereich, fach]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadBereich || !uploadFach) {
      toast.error("Bitte Bereich und Fach wählen");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Datei zu groß (max 20 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API}/meister/files?bereich=${uploadBereich}&fach=${uploadFach}&display_name=${encodeURIComponent(file.name)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Datei hochgeladen");
      e.target.value = "";
      load();
      onUpload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const renameFile = async (id, currentName) => {
    const newName = window.prompt("Neuer Name:", currentName);
    if (!newName || newName === currentName) return;
    try {
      await axios.patch(`${API}/meister/files/${id}?display_name=${encodeURIComponent(newName)}`);
      toast.success("Umbenannt");
      load();
    } catch {
      toast.error("Umbenennen fehlgeschlagen");
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm("Datei wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/meister/files/${id}`);
      toast.success("Gelöscht");
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}/meister/files/${file.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.original_filename || file.display_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const subjectLabel = bereich && fach && subjects?.[bereich]?.subjects?.[fach];

  return (
    <Card className="bg-card border-border" data-testid="knowledge-sidebar">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          Dateien &amp; Wissen
        </CardTitle>
        {subjectLabel && (
          <p className="text-xs text-muted-foreground">Fach: {subjectLabel}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {isAdmin && (
          <div className="space-y-2 p-3 rounded-sm bg-secondary/30 border border-border">
            <p className="text-xs font-medium">Neue Datei hochladen</p>
            <div className="grid grid-cols-2 gap-1">
              <Select value={uploadBereich} onValueChange={(v) => { setUploadBereich(v); setUploadFach(Object.keys(subjects?.[v]?.subjects || {})[0]); }}>
                <SelectTrigger className="h-8 text-xs bg-background" data-testid="upload-bereich-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subjects && Object.entries(subjects).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={uploadFach} onValueChange={setUploadFach}>
                <SelectTrigger className="h-8 text-xs bg-background" data-testid="upload-fach-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subjects?.[uploadBereich] &&
                    Object.entries(subjects[uploadBereich].subjects).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.docx,.doc,.txt,.md,.csv,.json,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.xls"
              onChange={handleUpload}
              data-testid="file-upload-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              size="sm"
              className="w-full gap-2 h-8"
              data-testid="file-upload-btn"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? "Lade hoch..." : "Datei auswählen"}
            </Button>
            <p className="text-[10px] text-muted-foreground">
              PDF, Word, Text, CSV, Bilder. Max. 20 MB.
            </p>
          </div>
        )}

        <div>
          <p className="text-xs font-medium mb-2">
            {bereich && fach ? "Materialien für dieses Fach" : "Alle Materialien"} ({files.length})
          </p>
          <ScrollArea className="h-[280px] pr-2">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : files.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">
                Noch keine Dateien.
                {isAdmin && " Lade welche hoch, damit der Agent darauf zugreifen kann."}
              </p>
            ) : (
              <div className="space-y-1.5">
                {files.map((f) => (
                  <div
                    key={f.id}
                    className="group flex items-center gap-2 p-2 rounded-sm bg-secondary/30 border border-border hover:border-primary/40 transition-colors"
                    data-testid={`kfile-${f.id}`}
                  >
                    <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" title={f.display_name}>{f.display_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {Math.round((f.size_bytes || 0) / 1024)} KB
                        {f.has_text ? " · KI-lesbar" : " · Anhang"}
                      </p>
                    </div>
                    <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => downloadFile(f)} title="Download">
                        <Download className="h-3 w-3" />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => renameFile(f.id, f.display_name)} title="Umbenennen">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => deleteFile(f.id)} title="Löschen">
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
};

const MeisterAgentInner = () => {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const [subjects, setSubjects] = useState(null);
  const [bereich, setBereich] = useState(null);
  const [fach, setFach] = useState(null);
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/meister/subjects`).then((res) => setSubjects(res.data)).catch(() => {});
    fetchSessions();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/ai/meister-agent/sessions`);
      setSessions(res.data);
    } catch {}
  };

  const loadSession = async (s) => {
    try {
      const res = await axios.get(`${API}/ai/meister-agent/history`, { params: { session_id: s.session_id } });
      setSessionId(s.session_id);
      setBereich(s.bereich);
      setFach(s.fach);
      setMessages(res.data);
    } catch {
      toast.error("Verlauf konnte nicht geladen werden");
    }
  };

  const newChat = () => {
    setSessionId(generateSessionId());
    setMessages([]);
    setInput("");
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/ai/meister-agent/session/${sid}`);
      toast.success("Gelöscht");
      if (sid === sessionId) newChat();
      fetchSessions();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending || !bereich || !fach) return;

    const userMsg = { role: "user", content, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/ai/meister-agent`, {
        session_id: sessionId,
        bereich,
        fach,
        message: content,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: res.data.reply,
          created_at: res.data.created_at,
          knowledge_files_used: res.data.knowledge_files_used,
        },
      ]);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "KI-Antwort fehlgeschlagen");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = fach ? (SUGGESTIONS_BY_SUBJECT[fach] || []) : [];
  const subjectLabel = bereich && fach && subjects?.[bereich]?.subjects?.[fach];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]" data-testid="meister-agent-page">
      {/* Main Chat Area */}
      <div className="space-y-4 min-w-0">
        {/* Subject Selector */}
        {(!bereich || !fach) ? (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Wähle Bereich &amp; Fach
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SubjectPicker
                subjects={subjects}
                bereich={bereich}
                fach={fach}
                onSelectBereich={(b) => { setBereich(b); setFach(null); }}
                onSelectFach={(f) => { setFach(f); newChat(); }}
              />
            </CardContent>
          </Card>
        ) : (
          /* Chat */
          <Card className="bg-card border-border flex flex-col min-h-[600px]">
            <CardContent className="p-4 flex flex-col flex-1 gap-3">
              {/* Header */}
              <div className="flex items-center gap-3 pb-3 border-b border-border">
                <div className="w-10 h-10 rounded-sm bg-primary/15 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">S&amp;T Meister-Agent</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {subjects?.[bereich]?.label} → <span className="text-primary">{subjectLabel}</span>
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setFach(null); setMessages([]); }} data-testid="change-subject-btn">
                  Fach wechseln
                </Button>
                <Button size="sm" variant="ghost" onClick={newChat} className="gap-1" data-testid="new-meister-chat-btn">
                  <Plus className="h-3.5 w-3.5" /> Neu
                </Button>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto space-y-4 py-2 pr-1"
                data-testid="meister-messages"
              >
                {messages.length === 0 ? (
                  <div className="text-center py-8 space-y-4">
                    <div className="inline-flex w-14 h-14 rounded-full bg-primary/10 items-center justify-center">
                      <GraduationCap className="h-7 w-7 text-primary" />
                    </div>
                    <p className="text-base font-medium">
                      Bereit für {subjectLabel}.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Ich antworte prüfungsnah mit Erklärung, Formel, Beispiel, Rechenweg & ELI14.
                    </p>
                    {suggestions.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-2 max-w-3xl mx-auto pt-3">
                        {suggestions.map((s, idx) => (
                          <button
                            key={idx}
                            onClick={() => sendMessage(s)}
                            className="text-left text-xs px-3 py-2 rounded-sm border border-border bg-secondary/30 hover:bg-secondary/60 transition-colors"
                            data-testid={`meister-suggestion-${idx}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  messages.map((m, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                      data-testid={`meister-msg-${m.role}`}
                    >
                      <div
                        className={`w-8 h-8 rounded-sm shrink-0 flex items-center justify-center ${
                          m.role === "user" ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"
                        }`}
                      >
                        {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                      <div
                        className={`rounded-sm px-3 py-2 max-w-[88%] min-w-0 ${
                          m.role === "user" ? "bg-primary/10" : "bg-secondary/40"
                        }`}
                      >
                        <ContentRenderer content={m.content} role={m.role} />
                        {m.knowledge_files_used && m.knowledge_files_used.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-border/50">
                            <p className="text-[10px] text-muted-foreground">📚 Quellen: {m.knowledge_files_used.join(", ")}</p>
                          </div>
                        )}
                        {m.created_at && (
                          <div className="text-[10px] text-muted-foreground mt-1">
                            {format(new Date(m.created_at), "dd.MM. HH:mm", { locale: de })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                {sending && (
                  <div className="flex gap-3" data-testid="meister-loading">
                    <div className="w-8 h-8 rounded-sm bg-primary/15 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="bg-secondary/40 rounded-sm px-3 py-2 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Meister-Agent denkt nach…</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Deine Frage</span>
                  <Button
                    onClick={() => sendMessage()}
                    size="sm"
                    className="gap-2 h-8"
                    disabled={sending || !input.trim()}
                    data-testid="meister-send-btn"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {sending ? "Sendet…" : "Senden"}
                  </Button>
                </div>
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="z. B. Berechne die Schnittgeschwindigkeit beim Drehen…"
                  rows={3}
                  className="bg-background border-input resize-none"
                  disabled={sending}
                  data-testid="meister-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sessions */}
        {sessions.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Letzte Unterhaltungen</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[120px]">
                <div className="space-y-1 pr-2">
                  {sessions.map((s) => (
                    <div
                      key={s.session_id}
                      onClick={() => loadSession(s)}
                      className={`group flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer transition-colors ${
                        s.session_id === sessionId ? "bg-primary/15 text-primary" : "hover:bg-secondary/50"
                      }`}
                      data-testid={`meister-session-${s.session_id}`}
                    >
                      <Bot className="h-3 w-3 shrink-0" />
                      <span className="text-xs truncate flex-1">
                        [{s.fach}] {s.last_message?.slice(0, 40) || "Neuer Chat"}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 opacity-0 group-hover:opacity-100"
                        onClick={(e) => deleteSession(s.session_id, e)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Right Sidebar: Knowledge Files */}
      <div>
        <KnowledgeSidebar
          bereich={bereich}
          fach={fach}
          isAdmin={isAdmin}
          subjects={subjects}
          onUpload={() => {}}
        />
      </div>
    </div>
  );
};

const MeisterAgent = () => {
  const { user } = useAuth();
  const isStaff = ["admin", "super_admin", "dozent", "auditor"].includes(user?.role);

  if (isStaff) {
    return (
      <AdminLayout title="S&T Meister-Agent">
        <MeisterAgentInner />
      </AdminLayout>
    );
  }
  return (
    <ParticipantLayout title="S&T Meister-Agent">
      <MeisterAgentInner />
    </ParticipantLayout>
  );
};

export default MeisterAgent;
